import React, { useState } from 'react';
import {
  Input,
  InputNumber,
  Segmented,
  Switch,
  Select,
  Button,
  Popconfirm,
  Tag,
  Collapse,
  Radio,
  Tooltip,
  Empty,
} from 'antd';
import {
  LockOutlined,
  DeleteOutlined,
  ExperimentOutlined,
} from '@ant-design/icons';
import { useDispatch } from 'react-redux';

import {
  DevAssetAdmin,
  DevNodeAdmin,
  DevPlanContent,
  TIER_OPTIONS,
  patchNode,
  deleteNode,
  createEdge,
  deleteEdge,
  patchEdge,
  createNodeGame,
  deleteNodeGame,
  patchRemediation,
  deleteRemediation,
} from 'state';

import { usePatchBuffer } from '../hooks';
import { Field, Section, LockedRow } from './inspectorBits';

type Props = {
  node: DevNodeAdmin;
  planCode: string;
  content: DevPlanContent;
  assets: DevAssetAdmin[];
  editable: boolean;
  onSelectNodeId: (id: number) => void;
};

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

const RANKS = ['1', '2', '3'];
const DIFF_COLOR: Record<string, string> = {
  Easy: 'green',
  Moderate: 'gold',
  Hard: 'volcano',
  'Very hard': 'red',
};

const NodeInspector: React.FC<Props> = ({
  node,
  planCode,
  content,
  assets,
  editable,
  onSelectNodeId,
}) => {
  const dispatch = useDispatch();

  // Free-text fields: local state + debounced PATCH (optimistic autosave).
  const [name, setName] = useState(node.name);
  const [description, setDescription] = useState(node.description);
  const { push } = usePatchBuffer<{ name: string; description: string }>((p) =>
    dispatch(patchNode({ id: node.id, plan: planCode, ...p }))
  );
  // Discrete controls PATCH immediately.
  const patch = (p: Record<string, unknown>) =>
    dispatch(patchNode({ id: node.id, plan: planCode, ...p }));

  const tracks = [...content.tracks].sort((a, b) => a.order - b.order);
  const groupOptions = content.clusters
    .filter((c) => c.track === node.track)
    .sort((a, b) => a.order - b.order)
    .map((c) => ({ value: c.code, label: `${c.code} · ${c.name}` }));

  const nodeLabel = (id: number) => {
    const n = content.nodes.find((x) => x.id === id);
    return n ? `${n.code} · ${n.name}` : String(id);
  };
  const otherNodes = content.nodes.filter((n) => n.id !== node.id);

  const prereqs = content.edges.filter(
    (e) => e.to_node === node.id && e.kind === 'prereq'
  );
  const softs = content.edges.filter(
    (e) => e.from_node === node.id && e.kind === 'soft'
  );
  const prereqIds = new Set(prereqs.map((e) => e.from_node));
  const softIds = new Set(softs.map((e) => e.to_node));

  const gameAssets = assets.filter((a) => a.kind === 'game');
  const assetTitle = (id: number | null) =>
    id == null ? '—' : assets.find((a) => a.id === id)?.title ?? String(id);
  const nodeGames = content.node_games.filter((g) => g.node === node.id);
  const usedGameIds = new Set(nodeGames.map((g) => g.game_asset));

  const routes = content.remediation.filter((r) => r.node === node.id);

  // Assessment-backed nodes (Idea Generator) play PM blueprints instead of a
  // scenario HTML: detail carries the cluster's list and the rank -> slug ladder.
  const blueprints = node.detail?.blueprints ?? [];
  const ladder = node.detail?.rank_ladder ?? {};
  const bySlug = new Map(blueprints.map((b) => [b.slug, b]));

  return (
    <div className="devadmin-insp">
      <div className="devadmin-insp__head">
        <div>
          <div className="devadmin-insp__kind">Node</div>
          <div className="devadmin-insp__title">{name}</div>
        </div>
        <Tag className="devadmin-insp__codechip">{node.code}</Tag>
      </div>

      <Section title="Basics">
        <Field
          label="Name"
          info="The node's title on the tree card and detail panel. Display only — no effect on unlocking or scoring."
        >
          <Input
            value={name}
            disabled={!editable}
            onChange={(e) => {
              setName(e.target.value);
              push({ name: e.target.value });
            }}
          />
        </Field>
        <Field
          label="Description"
          info="Longer text shown in the node's detail panel when a trainee selects it. Display only."
        >
          <Input.TextArea
            value={description}
            disabled={!editable}
            autoSize={{ minRows: 2, maxRows: 6 }}
            onChange={(e) => {
              setDescription(e.target.value);
              push({ description: e.target.value });
            }}
          />
        </Field>
        <LockedRow
          label="Display code"
          value={node.code}
          why="The badge shown on the node. It's meant to be an editable label, but the trainee renderer currently JOINS a node to its links, progress and games by this exact string (which today equals the internal code). Editing it would break a published tree until the front-end is migrated to key on the internal code — so it's locked for now."
        />
        <Field
          label="Tier"
          info={
            <>
              Sets the XP earned per rank passed, via a fixed ladder:
              Foundational 200/300/450, Core 400/600/900, Advanced 700/1050,
              Mastery 1200/1800. The ladder length also implies the expected
              number of ranks (3 for Foundational/Core, 2 for Advanced/Mastery):
              if the node's max rank exceeds the ladder, those extra ranks grant{' '}
              <b>0 XP</b>. Keep the tier consistent with the scenario's rank
              count. Also drives the tier badge.
            </>
          }
        >
          <Segmented
            value={node.tier}
            disabled={!editable}
            options={TIER_OPTIONS.map((t) => ({ label: cap(t), value: t }))}
            onChange={(v) => patch({ tier: v })}
          />
        </Field>
        <Field
          label="Required level"
          hint="unlock gate"
          info={
            <>
              The profile level a trainee needs before the node shows as ready to
              unlock. This is a <b>front-end display gate only</b> — the backend
              is permissive and will still record a pass/rank for any completed
              scenario regardless of level. Use it to pace what looks reachable,
              not to hard-block scoring.
            </>
          }
        >
          <InputNumber
            min={0}
            value={node.required_level}
            disabled={!editable}
            onChange={(v) => v != null && patch({ required_level: v })}
          />
        </Field>
        <Field
          label="Active"
          hint="show in the tree"
          info={
            <>
              Whether the node is part of the tree. Inactive nodes are omitted
              entirely from the trainee view (not shown, not launchable). Note:
              publishing is blocked while any link or game still points at an
              inactive node — clean those up first.
            </>
          }
        >
          <Switch
            checked={node.is_active}
            disabled={!editable}
            onChange={(v) => patch({ is_active: v })}
          />
        </Field>
      </Section>

      <Section title="Placement">
        <Field
          label="Track"
          info={
            <>
              Which of the four tracks the node lives in — changes its lane and
              position on the tree and its accent color. (You can also just drag
              the node into another track on the canvas.) No effect on scoring.
            </>
          }
        >
          <Select
            value={node.track}
            disabled={!editable}
            style={{ width: '100%' }}
            options={tracks.map((t) => ({ value: t.id, label: t.name }))}
            onChange={(v) => patch({ track: v })}
          />
        </Field>
        <Field
          label="Cluster / group"
          info={
            <>
              Which cluster within the track the node belongs to. Controls its
              ordering within the track layer and the cluster name shown on the
              node's detail subtitle. No effect on scoring.
            </>
          }
        >
          <Select
            value={node.group}
            disabled={!editable}
            style={{ width: '100%' }}
            options={groupOptions}
            onChange={(v) => patch({ group: v })}
          />
        </Field>
      </Section>

      {blueprints.length > 0 && (
        <Section
          title="Scenarios"
          hint={`${blueprints.length} blueprint${blueprints.length === 1 ? '' : 's'}`}
          info={
            <>
              The PM blueprints this node is played on — its cluster's scenarios,
              ordered easy to hard. The <b>rank ladder</b> picks which one a
              trainee gets at each rank. Seeded from the scenario-clusters
              workbook; read-only here.
            </>
          }
        >
          <div className="devadmin-chiplist">
            {RANKS.map((rank) => {
              const slug = ladder[rank];
              if (!slug) return null;
              const bp = bySlug.get(slug);
              return (
                <div key={rank} className="devadmin-edgerow">
                  <Tag color="purple">Rank {rank}</Tag>
                  <Tooltip title={slug}>
                    <span className="devadmin-edgerow__name">
                      {bp ? bp.name : slug}
                    </span>
                  </Tooltip>
                  {bp && <Tag color={DIFF_COLOR[bp.difficulty] || 'default'}>{bp.difficulty}</Tag>}
                </div>
              );
            })}
          </div>
          <Collapse
            ghost
            className="devadmin-advanced"
            items={[
              {
                key: 'bps',
                label: <span>All {blueprints.length} scenarios in this cluster</span>,
                children: (
                  <div className="devadmin-chiplist">
                    {blueprints.map((bp) => (
                      <div key={bp.slug} className="devadmin-edgerow">
                        <Tooltip title={bp.slug}>
                          <span className="devadmin-edgerow__name">{bp.name}</span>
                        </Tooltip>
                        <Tag color={DIFF_COLOR[bp.difficulty] || 'default'}>
                          {bp.difficulty}
                        </Tag>
                        <span className="devadmin-muted">
                          {bp.assets} asset{bp.assets === 1 ? '' : 's'} · {bp.news} news
                        </span>
                      </div>
                    ))}
                  </div>
                ),
              },
            ]}
          />
        </Section>
      )}

      <Section
        title="Prerequisites"
        hint="gate this node"
        info={
          <>
            Nodes that must be reached before this one appears unlockable to
            trainees (a <b>front-end</b> gate; the backend doesn't enforce it).
            Same-track prerequisites also push this node deeper into its track
            layer on the canvas; cross-track ones are drawn as long links.
          </>
        }
      >
        <div className="devadmin-chiplist">
          {prereqs.map((e) => (
            <div key={e.id} className="devadmin-edgerow">
              <span className="devadmin-edgerow__name">{nodeLabel(e.from_node)}</span>
              <Tooltip title="Intended 'rank of the prerequisite required'. NOT enforced by the trainee yet — any rank ≥ 1 satisfies a prerequisite today. Stored for a future gate.">
                <InputNumber
                  size="small"
                  min={1}
                  value={e.required_rank}
                  disabled={!editable}
                  onChange={(v) =>
                    v != null &&
                    dispatch(patchEdge({ id: e.id, plan: planCode, required_rank: v }))
                  }
                />
              </Tooltip>
              {editable && (
                <Button
                  size="small"
                  type="text"
                  icon={<DeleteOutlined />}
                  onClick={() => dispatch(deleteEdge({ id: e.id, plan: planCode }))}
                />
              )}
            </div>
          ))}
          {prereqs.length === 0 && (
            <div className="devadmin-muted">No prerequisites (unlocks from core).</div>
          )}
        </div>
        {editable && (
          <Select
            showSearch
            placeholder="+ Add prerequisite"
            value={null}
            style={{ width: '100%', marginTop: 6 }}
            optionFilterProp="label"
            options={otherNodes
              .filter((n) => !prereqIds.has(n.id))
              .map((n) => ({ value: n.id, label: `${n.code} · ${n.name}` }))}
            onChange={(v) =>
              dispatch(
                createEdge({
                  plan: planCode,
                  from_node: v,
                  to_node: node.id,
                  kind: 'prereq',
                  required_rank: 1,
                })
              )
            }
          />
        )}
      </Section>

      <Section
        title="Recommends (soft)"
        hint="non-blocking"
        info="Suggested next nodes. Drawn as faint dashed links and listed on the node — they never gate unlocking or affect scoring; purely advisory."
      >
        <div className="devadmin-chiplist">
          {softs.map((e) => (
            <Tag
              key={e.id}
              closable={editable}
              onClose={(ev) => {
                ev.preventDefault();
                dispatch(deleteEdge({ id: e.id, plan: planCode }));
              }}
            >
              {nodeLabel(e.to_node)}
            </Tag>
          ))}
          {softs.length === 0 && <div className="devadmin-muted">None.</div>}
        </div>
        {editable && (
          <Select
            showSearch
            placeholder="+ Recommend a node"
            value={null}
            style={{ width: '100%', marginTop: 6 }}
            optionFilterProp="label"
            options={otherNodes
              .filter((n) => !softIds.has(n.id))
              .map((n) => ({ value: n.id, label: `${n.code} · ${n.name}` }))}
            onChange={(v) =>
              dispatch(
                createEdge({
                  plan: planCode,
                  from_node: node.id,
                  to_node: v,
                  kind: 'soft',
                  required_rank: 1,
                })
              )
            }
          />
        )}
      </Section>

      <Section
        title="Games practiced here"
        info="Practice games surfaced at this node. A game becomes playable when any node serving it is unlocked or unlockable. Games don't grant node XP — they're practice, not scored ranks."
      >
        <div className="devadmin-chiplist">
          {nodeGames.map((g) => (
            <Tag
              key={g.id}
              icon={<ExperimentOutlined />}
              closable={editable}
              onClose={(ev) => {
                ev.preventDefault();
                dispatch(deleteNodeGame({ id: g.id, plan: planCode }));
              }}
            >
              {assetTitle(g.game_asset)}
              <span className="devadmin-muted"> · {g.role}</span>
            </Tag>
          ))}
          {nodeGames.length === 0 && <div className="devadmin-muted">None.</div>}
        </div>
        {editable && (
          <Select
            showSearch
            placeholder="+ Add game"
            value={null}
            style={{ width: '100%', marginTop: 6 }}
            optionFilterProp="label"
            options={gameAssets
              .filter((a) => !usedGameIds.has(a.id))
              .map((a) => ({ value: a.id, label: a.title }))}
            onChange={(v) =>
              dispatch(
                createNodeGame({
                  plan: planCode,
                  node: node.id,
                  game_asset: v,
                  role: 'primary',
                })
              )
            }
          />
        )}
      </Section>

      <Section
        title="Remediation"
        hint="re-wire / remove existing routes"
        info={
          <>
            When a trainee <b>fails a specific gate</b> in this node's scenario,
            they can be redirected to a practice game or another node. The gate
            itself is defined by the scenario HTML (locked); here you edit only
            its label and where it points. Adding new routes needs the gate-index
            (v1.5).
          </>
        }
      >
        {routes.length === 0 && (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No remediation routes on this node."
          />
        )}
        {routes.map((r) => {
          const mode: 'game' | 'node' = r.target_node != null ? 'node' : 'game';
          return (
            <div key={r.id} className="devadmin-route">
              <div className="devadmin-route__gate">
                <LockOutlined /> {r.gate_key}
              </div>
              <Input
                size="small"
                placeholder="Gate label"
                defaultValue={r.gate_name}
                disabled={!editable}
                onBlur={(e) =>
                  e.target.value !== r.gate_name &&
                  dispatch(
                    patchRemediation({
                      id: r.id,
                      plan: planCode,
                      gate_name: e.target.value,
                    })
                  )
                }
              />
              <div className="devadmin-route__target">
                <Radio.Group
                  size="small"
                  value={mode}
                  disabled={!editable}
                  onChange={(e) =>
                    dispatch(
                      patchRemediation({
                        id: r.id,
                        plan: planCode,
                        ...(e.target.value === 'game'
                          ? { target_node: null }
                          : { target_game: null }),
                      })
                    )
                  }
                  options={[
                    { label: 'Game', value: 'game' },
                    { label: 'Node', value: 'node' },
                  ]}
                  optionType="button"
                />
                {mode === 'game' ? (
                  <Select
                    showSearch
                    size="small"
                    placeholder="Redirect to game"
                    value={r.target_game}
                    disabled={!editable}
                    style={{ flex: 1, minWidth: 120 }}
                    optionFilterProp="label"
                    options={gameAssets.map((a) => ({ value: a.id, label: a.title }))}
                    onChange={(v) =>
                      dispatch(
                        patchRemediation({
                          id: r.id,
                          plan: planCode,
                          target_game: v,
                          target_node: null,
                        })
                      )
                    }
                  />
                ) : (
                  <Select
                    showSearch
                    size="small"
                    placeholder="Redirect to node"
                    value={r.target_node}
                    disabled={!editable}
                    style={{ flex: 1, minWidth: 120 }}
                    optionFilterProp="label"
                    options={content.nodes.map((n) => ({
                      value: n.id,
                      label: `${n.code} · ${n.name}`,
                    }))}
                    onChange={(v) =>
                      dispatch(
                        patchRemediation({
                          id: r.id,
                          plan: planCode,
                          target_node: v,
                          target_game: null,
                        })
                      )
                    }
                  />
                )}
                {editable && (
                  <Button
                    size="small"
                    type="text"
                    icon={<DeleteOutlined />}
                    onClick={() =>
                      dispatch(deleteRemediation({ id: r.id, plan: planCode }))
                    }
                  />
                )}
              </div>
            </div>
          );
        })}
        <Tooltip title="Adding a new route needs the gate-index (deferred to v1.5)">
          <Button disabled style={{ marginTop: 6 }}>
            + Add route (v1.5)
          </Button>
        </Tooltip>
      </Section>

      <Collapse
        ghost
        className="devadmin-advanced"
        items={[
          {
            key: 'adv',
            label: (
              <span>
                <LockOutlined /> Advanced (read-only)
              </span>
            ),
            children: (
              <>
                <LockedRow
                  label="Internal code"
                  value={node.internal_code}
                  why="The immutable identity baked into the scenario HTML (its refId). Scoring and the trainee's progress/links/games all key on this. Can't change without re-authoring the HTML."
                />
                <LockedRow
                  label="Scenario asset"
                  value={assetTitle(node.scenario_asset)}
                  why="The HTML mini-app that scores this node. Assigning or swapping it is a v2 operation — the file must emit a refId matching this node's internal code."
                />
                <LockedRow
                  label="Max rank"
                  value={String(node.max_rank)}
                  why="How many ranks the node has (the pips). Fixed by the scenario HTML, which emits a set number of ranks; the backend rejects any rank above this. Editing it here without changing the HTML would desync."
                />
                <LockedRow
                  label="Skill points cost"
                  value={String(node.skill_points_cost)}
                  why="Legacy 'skill point' cost. Skill points are never granted or charged today, so this only tweaks a hint string — it doesn't gate anything. Left read-only."
                />
                <LockedRow
                  label="Meter"
                  value={node.meter_name || '—'}
                  why="Optional behavioral-meter label, shown as a small tag on the node's detail panel and captured for a future analytics view. No gating effect."
                />
                <LockedRow
                  label="Layout order"
                  value={String(node.order)}
                  why="A stored ordering hint the trainee renderer currently ignores (it lays out by prerequisite depth and cluster). No visible effect."
                />
              </>
            ),
          },
        ]}
      />

      {editable && (
        <div className="devadmin-insp__danger">
          <Popconfirm
            title="Delete this node?"
            description="Removes the node and its links/games/remediation from this draft."
            okText="Delete"
            okButtonProps={{ danger: true }}
            onConfirm={() => {
              dispatch(deleteNode({ id: node.id, plan: planCode }));
              onSelectNodeId(-1);
            }}
          >
            <Button danger icon={<DeleteOutlined />}>
              Delete node
            </Button>
          </Popconfirm>
        </div>
      )}
    </div>
  );
};

export default NodeInspector;
